import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Calculator, CircleNotch, ArrowRight, Plus, Trash, ArrowUpRight, Crosshair, ArrowsLeftRight, Star, FloppyDisk, UploadSimple, DownloadSimple, Code,
} from "@phosphor-icons/react";

const inputCls = "h-8 px-2 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[12px]";
const QUADS = ["NE", "SE", "SW", "NW"];

const PointInputs = ({ label, value, onChange, testid, points = [], onLoad }) => (
  <div className="flex items-center gap-2">
    <span className="w-16 text-[11px] font-mono text-[var(--text-dim)]">{label}</span>
    <Input value={value.n} onChange={(e) => onChange({ ...value, n: e.target.value })} placeholder="North" className={inputCls} data-testid={`${testid}-n`} />
    <Input value={value.e} onChange={(e) => onChange({ ...value, e: e.target.value })} placeholder="East" className={inputCls} data-testid={`${testid}-e`} />
    <select value="" onChange={(e) => { const p = points.find((x) => x.id === e.target.value); if (p) onLoad({ n: String(p.n), e: String(p.e) }); }}
      title="Load a saved point" data-testid={`${testid}-load`}
      className="h-8 px-1 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[11px] text-[var(--text-dim)] max-w-[70px]">
      <option value="">★</option>
      {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  </div>
);

const BearingInputs = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1.5">
    <select value={value.quad} onChange={(e) => onChange({ ...value, quad: e.target.value })} data-testid={`${testid}-quad`}
      className="h-8 px-1.5 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[12px] text-[var(--text)]">
      {QUADS.map((q) => <option key={q} value={q}>{q}</option>)}
    </select>
    <Input value={value.d} onChange={(e) => onChange({ ...value, d: e.target.value })} placeholder="°" className={`${inputCls} w-14`} data-testid={`${testid}-d`} />
    <Input value={value.m} onChange={(e) => onChange({ ...value, m: e.target.value })} placeholder="'" className={`${inputCls} w-14`} data-testid={`${testid}-m`} />
    <Input value={value.s} onChange={(e) => onChange({ ...value, s: e.target.value })} placeholder='"' className={`${inputCls} w-14`} data-testid={`${testid}-s`} />
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-[12px] font-mono py-0.5 border-b border-[var(--border)]/30">
    <span className="text-[var(--text-dim)]">{k}</span><span className="text-[var(--text)] font-bold">{v}</span>
  </div>
);

export const CogoCalc = ({ onUsePrompt, clientStandards }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Saved point store (owner-scoped, reused across tabs)
  const [points, setPoints] = useState([]);
  const [sName, setSName] = useState("");
  const [sN, setSN] = useState("");
  const [sE, setSE] = useState("");
  const [sZ, setSZ] = useState("");
  const [sDesc, setSDesc] = useState("");
  const [importOrder, setImportOrder] = useState("PNEZD");
  const [importing, setImporting] = useState(false);
  const [ptBlock, setPtBlock] = useState("");
  const fileRef = useRef();

  const importPoints = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const t = toast.loading("Importing survey points…");
    try {
      const r = await api.importCogoPoints(file, importOrder);
      setPoints(r.points || []);
      toast.success(`Imported ${r.imported} point${r.imported === 1 ? "" : "s"}`, { id: t });
    } catch { toast.error("Could not import the point file.", { id: t }); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const loadPoints = async () => {
    try { setPoints(await api.cogoPoints()); } catch (e) { console.debug("cogoPoints load failed (best-effort)", e); }
  };
  useEffect(() => { if (open) loadPoints(); }, [open]);

  const savePoint = async () => {
    if (!sName.trim()) { toast.error("Name the point first."); return; }
    try {
      await api.saveCogoPoint({ name: sName.trim(), n: Number(sN) || 0, e: Number(sE) || 0, z: sZ === "" ? null : Number(sZ), desc: sDesc.trim() });
      setSName(""); setSN(""); setSE(""); setSZ(""); setSDesc("");
      await loadPoints();
      toast.success("Point saved");
    } catch { toast.error("Could not save the point."); }
  };
  const removePoint = async (id) => {
    try { await api.deleteCogoPoint(id); await loadPoints(); } catch { toast.error("Could not delete."); }
  };
  const exportPoints = async () => {
    if (!points.length) { toast.error("No points to export."); return; }
    try {
      const blob = await api.exportCogoPoints(importOrder);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cogo_points_${importOrder}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${points.length} points (${importOrder})`);
    } catch { toast.error("Could not export points."); }
  };
  const downloadLisp = async () => {
    if (!points.length) { toast.error("Save or import some points first."); return; }
    try {
      const blob = await api.cogoPointsLisp({
        block_name: ptBlock.trim(),
        point_layer: clientStandards?.layers?.point || "V-NODE",
        text_layer: clientStandards?.layers?.pointText || clientStandards?.layers?.text || "V-NODE-TEXT",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "plant_points.lsp"; a.click();
      URL.revokeObjectURL(url);
      toast.success(ptBlock.trim() ? `Generated LISP inserting block "${ptBlock.trim()}"` : `Generated LISP to plant ${points.length} points`);
    } catch { toast.error("Could not generate the points LISP."); }
  };

  const usePointsInGen = () => {
    if (!points.length) { toast.error("Save or import some points first."); return; }
    const lines = points.map((p) => `  ${p.name}: N ${p.n}, E ${p.e}${p.desc ? `  [${p.desc}]` : ""}`).join("\n");
    onUsePrompt?.(
      "Place these COGO points in the drawing on the client's point/COGO layer. For each point, insert a point marker at the given " +
      "North/East coordinate and add a TEXT label showing the point NAME/number and its DESCRIPTION/CODE (in brackets) next to it, " +
      "sized to the drawing scale. Reuse the client's point and label layers/text styles.\n\nPoints (name: N, E [description]):\n" + lines
    );
    setOpen(false);
    toast.success(`${points.length} point${points.length === 1 ? "" : "s"} sent to the generator prompt`);
  };

  // Inverse
  const [invP1, setInvP1] = useState({ n: "", e: "" });
  const [invP2, setInvP2] = useState({ n: "", e: "" });
  const [invRes, setInvRes] = useState(null);

  // Traverse
  const [trStart, setTrStart] = useState({ n: "0", e: "0" });
  const [trCourses, setTrCourses] = useState([{ quad: "NE", d: "", m: "", s: "", dist: "" }]);
  const [trRes, setTrRes] = useState(null);

  // Intersection
  const [ixMode, setIxMode] = useState("bb");
  const [ixP1, setIxP1] = useState({ n: "", e: "" });
  const [ixP2, setIxP2] = useState({ n: "", e: "" });
  const [ixD1, setIxD1] = useState({ quad: "NE", d: "", m: "", s: "" });
  const [ixD2, setIxD2] = useState({ quad: "NE", d: "", m: "", s: "" });
  const [ixDist, setIxDist] = useState("");
  const [ixRes, setIxRes] = useState(null);

  const num = (o) => ({ n: Number(o.n) || 0, e: Number(o.e) || 0 });
  const brg = (o) => ({ quad: o.quad, d: Number(o.d) || 0, m: Number(o.m) || 0, s: Number(o.s) || 0 });

  const runInverse = async () => {
    setBusy(true);
    try { setInvRes(await api.cogoInverse(num(invP1), num(invP2))); }
    catch { toast.error("Could not compute the inverse."); }
    finally { setBusy(false); }
  };

  const runTraverse = async () => {
    setBusy(true);
    try {
      const courses = trCourses.map((c) => ({ ...brg(c), dist: Number(c.dist) || 0 }));
      setTrRes(await api.cogoTraverse(num(trStart), courses));
    } catch { toast.error("Could not compute the traverse."); }
    finally { setBusy(false); }
  };

  const runIntersect = async () => {
    setBusy(true);
    try {
      const payload = { mode: ixMode, p1: num(ixP1), p2: num(ixP2), dir1: brg(ixD1) };
      if (ixMode === "bb") payload.dir2 = brg(ixD2);
      else payload.dist2 = Number(ixDist) || 0;
      const r = await api.cogoIntersect(payload);
      setIxRes(r);
      if (!r.solutions?.length && r.message) toast.message(r.message);
    } catch { toast.error("Could not compute the intersection."); }
    finally { setBusy(false); }
  };

  const editCourse = (i, field, val) => setTrCourses((cs) => cs.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const addCourse = () => setTrCourses((cs) => [...cs, { quad: "NE", d: "", m: "", s: "", dist: "" }]);
  const rmCourse = (i) => setTrCourses((cs) => cs.filter((_, idx) => idx !== i));

  const sendTraverse = () => {
    if (!trRes?.courses?.length) return;
    const lines = trRes.courses.map((c) => `${c.i}. ${c.bearing}  ${c.distance} ft  ->  N ${c.north}, E ${c.east}`).join("\n");
    onUsePrompt?.(`Draw this traverse from start point (N ${trStart.n}, E ${trStart.e}) on the client's boundary layer. Courses (bearing, distance, computed coordinate):\n${lines}\nLabel each course with bearing + distance, place COGO points at each vertex, and report closure (${trRes.closure_ft} ft, ${trRes.precision}).`);
    setOpen(false);
    toast.success("Traverse sent to the generator prompt");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="cogo-calc-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Calculator size={14} weight="bold" /> COGO Calculator
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="cogo-calc-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Calculator size={18} weight="bold" className="text-[var(--primary)]" />COGO Calculator</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Coordinate geometry: inverse between points, traverse from bearings/distances, and intersections. North/East in feet.</DialogDescription>
        </DialogHeader>

        <div className="border border-[var(--border)] rounded-[3px] p-2 bg-[var(--surface-2)] flex flex-col gap-1.5" data-testid="cogo-point-store">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] w-16">Point store</span>
            <Input value={sName} onChange={(e) => setSName(e.target.value)} placeholder="name" className={`${inputCls} w-20`} data-testid="cogo-pt-name" />
            <Input value={sN} onChange={(e) => setSN(e.target.value)} placeholder="North" className={`${inputCls} w-20`} data-testid="cogo-pt-n" />
            <Input value={sE} onChange={(e) => setSE(e.target.value)} placeholder="East" className={`${inputCls} w-20`} data-testid="cogo-pt-e" />
            <Input value={sZ} onChange={(e) => setSZ(e.target.value)} placeholder="elev" className={`${inputCls} w-16`} data-testid="cogo-pt-z" />
            <Input value={sDesc} onChange={(e) => setSDesc(e.target.value)} placeholder="desc / code" className={`${inputCls} w-24`} data-testid="cogo-pt-desc" />
            <Button onClick={savePoint} size="sm" data-testid="cogo-pt-save"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><FloppyDisk size={13} />Save</Button>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] w-16">Import file</span>
            <select value={importOrder} onChange={(e) => setImportOrder(e.target.value)} data-testid="cogo-pt-order"
              className="h-8 px-1.5 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[11px] text-[var(--text)]">
              <option value="PNEZD">PNEZD</option>
              <option value="PENZD">PENZD</option>
            </select>
            <input type="file" ref={fileRef} accept=".csv,.txt" className="hidden" onChange={importPoints} data-testid="cogo-pt-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={importing} size="sm" variant="outline" data-testid="cogo-pt-import"
              className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]">
              {importing ? <CircleNotch size={13} className="animate-spin" /> : <UploadSimple size={13} />} Import PNEZD/PENZD
            </Button>
            <Button onClick={exportPoints} size="sm" variant="outline" data-testid="cogo-pt-export"
              className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]">
              <DownloadSimple size={13} /> Export
            </Button>
            <span className="text-[10px] font-mono text-[var(--text-dim)]">{points.length} saved</span>
          </div>
          {points.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center" data-testid="cogo-pt-list">
              {points.map((p) => (
                <span key={p.id} data-testid={`cogo-pt-chip-${p.name}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] text-[10px] font-mono text-[var(--text-dim)]">
                  <Star size={10} className="text-[var(--warn)]" />{p.name}
                  {p.desc ? <span className="text-[var(--primary)]">[{p.desc}]</span> : null}
                  <span className="text-[var(--text)]/60">({p.n},{p.e})</span>
                  <button onClick={() => removePoint(p.id)} className="hover:text-[var(--alert)]" data-testid={`cogo-pt-del-${p.name}`}><Trash size={10} /></button>
                </span>
              ))}
              <Button onClick={usePointsInGen} size="sm" variant="outline" data-testid="cogo-pt-use"
                className="h-6 rounded-[2px] text-[10px] gap-1 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] ml-auto"><ArrowRight size={11} />Draw {points.length} points + labels</Button>
              <Input value={ptBlock} onChange={(e) => setPtBlock(e.target.value)} placeholder="point block (optional)" className={`${inputCls} w-32`} data-testid="cogo-pt-block" />
              <Button onClick={downloadLisp} size="sm" variant="outline" data-testid="cogo-pt-lisp"
                className="h-6 rounded-[2px] text-[10px] gap-1 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><Code size={11} />Points LISP</Button>
            </div>
          )}
        </div>

        <Tabs defaultValue="inverse">
          <TabsList className="bg-[var(--surface-2)] rounded-[3px]">
            <TabsTrigger value="inverse" data-testid="cogo-tab-inverse" className="text-xs gap-1.5"><ArrowsLeftRight size={13} />Inverse</TabsTrigger>
            <TabsTrigger value="traverse" data-testid="cogo-tab-traverse" className="text-xs gap-1.5"><ArrowUpRight size={13} />Traverse</TabsTrigger>
            <TabsTrigger value="intersect" data-testid="cogo-tab-intersect" className="text-xs gap-1.5"><Crosshair size={13} />Intersection</TabsTrigger>
          </TabsList>

          <TabsContent value="inverse" className="flex flex-col gap-2 mt-3">
            <PointInputs label="From" value={invP1} onChange={setInvP1} testid="inv-p1" points={points} onLoad={(p) => setInvP1(p)} />
            <PointInputs label="To" value={invP2} onChange={setInvP2} testid="inv-p2" points={points} onLoad={(p) => setInvP2(p)} />
            <Button onClick={runInverse} disabled={busy} data-testid="cogo-inverse-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {busy ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} />} Compute inverse
            </Button>
            {invRes && (
              <div className="border border-[var(--border)] rounded-[3px] p-3 bg-[var(--surface-2)]" data-testid="cogo-inverse-result">
                <Row k="Bearing" v={invRes.bearing} />
                <Row k="Azimuth" v={`${invRes.azimuth}°`} />
                <Row k="Distance" v={`${invRes.distance} ft`} />
                <Row k="ΔN / ΔE" v={`${invRes.delta_n} / ${invRes.delta_e}`} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="traverse" className="flex flex-col gap-2 mt-3">
            <PointInputs label="Start" value={trStart} onChange={setTrStart} testid="tr-start" points={points} onLoad={(p) => setTrStart(p)} />
            <div className="flex flex-col gap-1.5 max-h-[220px] overflow-auto" data-testid="tr-courses">
              {trCourses.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`tr-course-${i}`}>
                  <span className="w-4 text-[11px] font-mono text-[var(--warn)]">{i + 1}</span>
                  <BearingInputs value={c} onChange={(v) => setTrCourses((cs) => cs.map((x, idx) => idx === i ? { ...x, ...v } : x))} testid={`tr-brg-${i}`} />
                  <Input value={c.dist} onChange={(e) => editCourse(i, "dist", e.target.value)} placeholder="dist ft" className={`${inputCls} w-20`} data-testid={`tr-dist-${i}`} />
                  <button onClick={() => rmCourse(i)} data-testid={`tr-rm-${i}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={addCourse} variant="outline" size="sm" data-testid="tr-add-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><Plus size={13} />Add course</Button>
              <Button onClick={runTraverse} disabled={busy} data-testid="cogo-traverse-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white flex-1">
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} />} Compute traverse
              </Button>
            </div>
            {trRes && (
              <div className="flex flex-col gap-2" data-testid="cogo-traverse-result">
                <div className="border border-[var(--border)] rounded-[3px] max-h-[150px] overflow-auto">
                  <div className="grid grid-cols-[24px_1fr_70px_80px_80px] gap-1 px-2 py-1 text-[10px] font-mono uppercase text-[var(--text-dim)] bg-[var(--surface-2)] sticky top-0">
                    <span>#</span><span>bearing</span><span>dist</span><span>N</span><span>E</span>
                  </div>
                  {trRes.courses.map((c) => (
                    <div key={c.i} className="grid grid-cols-[24px_1fr_70px_80px_80px] gap-1 px-2 py-0.5 text-[11px] font-mono border-t border-[var(--border)]/30">
                      <span className="text-[var(--warn)]">{c.i}</span><span className="truncate">{c.bearing}</span><span>{c.distance}</span><span>{c.north}</span><span>{c.east}</span>
                    </div>
                  ))}
                </div>
                <div className="border border-[var(--border)] rounded-[3px] p-2 bg-[var(--surface-2)]">
                  <Row k="Perimeter" v={`${trRes.perimeter_ft} ft`} />
                  <Row k="Closure" v={`${trRes.closure_ft} ft (${trRes.closure_bearing})`} />
                  <Row k="Precision" v={trRes.precision} />
                </div>
                <Button onClick={sendTraverse} data-testid="cogo-traverse-use-btn"
                  className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><ArrowRight size={14} />Use in generator</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="intersect" className="flex flex-col gap-2 mt-3">
            <div className="flex gap-2 text-[11px] font-mono">
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="ix-mode-bb">
                <input type="radio" checked={ixMode === "bb"} onChange={() => setIxMode("bb")} className="accent-[var(--primary)]" />Bearing–Bearing
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="ix-mode-bd">
                <input type="radio" checked={ixMode === "bd"} onChange={() => setIxMode("bd")} className="accent-[var(--primary)]" />Bearing–Distance
              </label>
            </div>
            <PointInputs label="Point 1" value={ixP1} onChange={setIxP1} testid="ix-p1" points={points} onLoad={(p) => setIxP1(p)} />
            <div className="flex items-center gap-2"><span className="w-16 text-[11px] font-mono text-[var(--text-dim)]">Bearing 1</span><BearingInputs value={ixD1} onChange={setIxD1} testid="ix-d1" /></div>
            <PointInputs label="Point 2" value={ixP2} onChange={setIxP2} testid="ix-p2" points={points} onLoad={(p) => setIxP2(p)} />
            {ixMode === "bb" ? (
              <div className="flex items-center gap-2"><span className="w-16 text-[11px] font-mono text-[var(--text-dim)]">Bearing 2</span><BearingInputs value={ixD2} onChange={setIxD2} testid="ix-d2" /></div>
            ) : (
              <div className="flex items-center gap-2"><span className="w-16 text-[11px] font-mono text-[var(--text-dim)]">Radius</span>
                <Input value={ixDist} onChange={(e) => setIxDist(e.target.value)} placeholder="distance ft from Point 2" className={inputCls} data-testid="ix-dist" /></div>
            )}
            <Button onClick={runIntersect} disabled={busy} data-testid="cogo-intersect-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {busy ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} />} Compute intersection
            </Button>
            {ixRes && (
              <div className="border border-[var(--border)] rounded-[3px] p-3 bg-[var(--surface-2)]" data-testid="cogo-intersect-result">
                {ixRes.solutions?.length ? ixRes.solutions.map((s, i) => (
                  <Row key={i} k={ixRes.solutions.length > 1 ? `Solution ${i + 1}` : "Intersection"} v={`N ${s.n}, E ${s.e}`} />
                )) : <span className="text-[11px] font-mono text-[var(--text-dim)]">{ixRes.message || "No solution."}</span>}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
